import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Heart, Eye, Award, ExternalLink } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="page-transition pt-28">
      {/* Hero — About header with magazine image embed */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Magazine page 24 — Akanni portrait screenshot */}
          <div className="lg:col-span-6">
            <div className="relative overflow-hidden group">
              <img
                src="/akanni_page24.png"
                alt="Akanni — Featured in Go!Entrepreneur Magazine, Page 24"
                className="w-full h-auto object-contain"
                data-testid="about-akanni-image"
              />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[#525252] text-xs font-mono tracking-wider">
                Go!Entrepreneur Magazine — Page 24
              </p>
              <a
                href="https://heyzine.com/flip-book/cbb8ee6956.html#page/24"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C5A059] text-xs font-mono tracking-wider hover:text-[#E0BE77] inline-flex items-center gap-1"
                data-testid="about-fullscreen-link"
              >
                View in Magazine <ExternalLink size={10} />
              </a>
            </div>
          </div>

          {/* Bio text */}
          <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              About the Artist
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#F5F5F0] tracking-tight leading-none mb-8">
              Akanni
            </h1>
            <div className="space-y-5 text-[#A3A3A3] leading-relaxed">
              <p className="text-[#F5F5F0] text-lg">
                I am Akanni, a visual artist whose work explores the boundaries between 
                the seen and unseen, the spoken and silent.
              </p>
              <p>
                Through bold color palettes and expressive brushwork, I seek to capture 
                moments of raw human emotion. Each painting in the Phaeton Imagery 
                collection is a meditation on this truth — an attempt to translate the 
                invisible language of feeling into something tangible.
              </p>
              <p>
                The name "Phaeton" comes from Greek mythology — the son of Helios who 
                dared to drive the sun chariot across the sky. Like Phaeton, I believe 
                in reaching for the impossible, in letting passion guide the brush even 
                when the path is uncertain.
              </p>
            </div>

            {/* Recognition badge */}
            <div className="mt-8 p-5 border border-[#C5A059]/30">
              <div className="flex items-start gap-4">
                <Award size={24} className="text-[#C5A059] flex-shrink-0" />
                <div>
                  <p className="text-[#F5F5F0] text-sm font-medium">
                    Named Top 10 Diverse Businesses in Art &amp; Culture
                  </p>
                  <p className="text-[#525252] text-xs font-mono mt-1">
                    Go!Entrepreneur Magazine
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Artist Statement */}
      <section className="py-16 bg-[#121212] border-y border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <blockquote className="font-serif text-2xl md:text-3xl italic text-[#F5F5F0] leading-relaxed mb-8">
            "But it's not just a portrait. His work is frequently 
            featuring bold strokes swirling across surfaces, cats 
            watching with glowing eyes, angels bending time 
            and light. Each figure carries a gentle, sometimes 
            fierce invitation to see ourselves reflected in 
            the natural and the spiritual."
          </blockquote>
          <footer className="font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059]">
            &mdash; Go!Entrepreneur Magazine
          </footer>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block mb-3">
            Philosophy
          </span>
          <h2 className="font-serif text-3xl md:text-4xl italic text-[#F5F5F0] mb-6">
            Art as a Bridge
          </h2>
          <p className="text-[#A3A3A3] text-base leading-relaxed">
            I believe that art serves as a bridge between souls — a way to communicate 
            truths that transcend language and culture.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-8 border border-white/10">
            <Palette size={28} className="text-[#C5A059] mb-5" strokeWidth={1} />
            <h3 className="font-serif text-xl text-[#F5F5F0] mb-3">Color as Emotion</h3>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              Every color carries emotional weight. I choose my palettes not for 
              aesthetic beauty alone, but for their ability to evoke feeling.
            </p>
          </div>
          <div className="p-8 border border-white/10">
            <Heart size={28} className="text-[#C5A059] mb-5" strokeWidth={1} />
            <h3 className="font-serif text-xl text-[#F5F5F0] mb-3">Authentic Expression</h3>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              I paint from a place of honesty. Each work is a piece of myself, 
              offered without pretense or calculation.
            </p>
          </div>
          <div className="p-8 border border-white/10">
            <Eye size={28} className="text-[#C5A059] mb-5" strokeWidth={1} />
            <h3 className="font-serif text-xl text-[#F5F5F0] mb-3">The Viewer's Journey</h3>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              A painting is not complete without its viewer. Your interpretation, 
              your emotional response — these are part of the work itself.
            </p>
          </div>
        </div>
      </section>

      {/* Based in */}
      <section className="py-16 bg-[#121212] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block mb-3">
                Studio
              </span>
              <h3 className="font-serif text-3xl text-[#F5F5F0] mb-4">Based in Fayetteville, GA</h3>
              <p className="text-[#A3A3A3] leading-relaxed mb-6">
                Working from Fayetteville, Georgia, Akanni draws inspiration from 
                the Southern landscape and its rich cultural tapestry. The studio is 
                where raw emotion meets canvas — a space dedicated to honest, 
                unfiltered creative expression.
              </p>
              <p className="text-[#A3A3A3] leading-relaxed">
                When you stand before one of my works, I want you to feel something — 
                perhaps something you can't quite name. That moment of recognition, of 
                connection between your inner world and what lives on the canvas, is what 
                drives my practice.
              </p>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_akanni-imagery/artifacts/a3gel2tr_IMG_1025.png"
                alt="Phaeton Imagery"
                className="w-full max-w-sm h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
        <h2 className="font-serif text-3xl text-[#F5F5F0] mb-8">Experience the Collection</h2>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/gallery"
            className="inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#0A0A0A] px-8 py-4 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
            data-testid="about-view-gallery"
          >
            View Gallery <ArrowRight size={16} />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-3 border border-white/20 text-[#F5F5F0] px-8 py-4 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
            data-testid="about-contact"
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
}
